import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, Info, Lock, RefreshCw, Save, ShieldCheck } from 'lucide-react';
import { adminService } from '../services/adminService';
import { AdminSetting } from '../types';
import { useToast } from '../contexts/ToastContext';
import {
  ECONOMY_SETTING_DEFINITIONS,
  EconomySettingDefinition,
  EconomySettingGroup,
  formatSettingUpdatedAt,
  getApiErrorMessage,
  parseEconomySettingValue,
} from '../utils/economySettings';

const ECONOMY_SETTING_GROUPS: EconomySettingGroup[] = [
  'Passes',
  'TP allocations',
  'Partner task names',
  'Standard partner stakes',
  'Pro partner stakes',
  'Partner availability',
  'Exchange rates',
];

interface LastSave {
  key: string;
  label: string;
  value: string;
  updatedAt: string | null;
  updatedBy: string | null;
  auditUnavailable: boolean;
}

const formatSettingValue = (setting: AdminSetting): string => setting.value;

const hasAuditMetadata = (setting: AdminSetting | undefined): boolean => {
  if (!setting?.updated_by?.trim() || !setting.updated_at) return false;
  return !Number.isNaN(new Date(setting.updated_at).getTime());
};

const getEconomyGroupId = (group: EconomySettingGroup): string =>
  `economy-group-${group.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

const EconomySettingsPanel: React.FC = () => {
  const toast = useToast();
  const [settings, setSettings] = useState<Record<string, AdminSetting>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [lastSave, setLastSave] = useState<LastSave | null>(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await adminService.getAdminSettings();
      const nextSettings: Record<string, AdminSetting> = {};
      const nextDrafts: Record<string, string> = {};

      data.forEach(setting => {
        nextSettings[setting.key] = setting;
        nextDrafts[setting.key] = setting.value;
      });

      setSettings(nextSettings);
      setDrafts(nextDrafts);
      setFieldErrors({});
      setLastSave(previous => {
        if (!previous?.auditUnavailable) return previous;
        return hasAuditMetadata(nextSettings[previous.key]) ? null : previous;
      });
    } catch (error) {
      setLoadError(getApiErrorMessage(error, 'Unable to load the economy settings.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const handleDraftChange = (definition: EconomySettingDefinition, rawValue: string) => {
    setDrafts(previous => ({ ...previous, [definition.key]: rawValue }));
    setFieldErrors(previous => {
      if (!previous[definition.key]) return previous;
      const next = { ...previous };
      delete next[definition.key];
      return next;
    });
  };

  const handleSaveSetting = async (definition: EconomySettingDefinition) => {
    const setting = settings[definition.key];
    if (!setting || definition.readOnly || savingKey !== null) return;

    const rawValue = drafts[definition.key] ?? setting.value;
    const result = parseEconomySettingValue(definition, rawValue);
    if (!result.parsed) {
      const message = result.error ?? `Enter a valid value for ${definition.label}.`;
      setFieldErrors(previous => ({ ...previous, [definition.key]: message }));
      return;
    }

    const confirmed = window.confirm(
      definition.valueType === 'string'
        ? `Save ${definition.label} as "${result.parsed.canonicalValue}"? Users see it the next time they open the task list.`
        : `Save ${definition.label} as ${result.parsed.canonicalValue} ${definition.unit}? ` +
            'This changes the value used by new sessions and future calculations.'
    );
    if (!confirmed) return;

    setFieldErrors(previous => {
      const next = { ...previous };
      delete next[definition.key];
      return next;
    });
    setSavingKey(definition.key);

    try {
      let savedSetting: AdminSetting;
      let auditUnavailable = false;

      if (definition.secureDailyPassEndpoint) {
        if (typeof result.parsed.value !== 'number') {
          throw new Error('Daily free passes must be submitted as a whole number.');
        }
        const response = await adminService.setDailyPasses(result.parsed.value);
        try {
          savedSetting = await adminService.getAdminSetting(definition.key);
          auditUnavailable = !hasAuditMetadata(savedSetting);
        } catch {
          // The write succeeded, but preserve that result if the metadata
          // refresh is temporarily unavailable. The next manual refresh will
          // reconcile the timestamp and audit actor.
          auditUnavailable = true;
          savedSetting = {
            ...setting,
            value: String(response.free_passes_per_day),
            typed_value: response.free_passes_per_day,
            updated_at: null,
            updated_by: null,
          };
        }
      } else {
        savedSetting = await adminService.updateAdminSetting(
          definition.key,
          result.parsed.value
        );
        auditUnavailable = !hasAuditMetadata(savedSetting);
      }

      setSettings(previous => ({ ...previous, [definition.key]: savedSetting }));
      setDrafts(previous => ({
        ...previous,
        [definition.key]: savedSetting.value,
      }));

      setLastSave({
        key: definition.key,
        label: definition.label,
        value: formatSettingValue(savedSetting),
        updatedAt: savedSetting.updated_at,
        updatedBy: savedSetting.updated_by,
        auditUnavailable,
      });

      const updatedAt = formatSettingUpdatedAt(savedSetting.updated_at);
      if (auditUnavailable) {
        toast.success(
          'Setting saved',
          `${definition.label} was saved. Refresh to retrieve its audit timestamp and actor.`
        );
      } else {
        toast.success(
          'Setting saved',
          `Audit recorded ${savedSetting.updated_by ? `for ${savedSetting.updated_by}` : 'for the current admin'} at ${updatedAt}.`
        );
      }
    } catch (error) {
      const message = getApiErrorMessage(
        error,
        `Unable to save ${definition.label.toLowerCase()}.`
      );
      setFieldErrors(previous => ({ ...previous, [definition.key]: message }));
      toast.error('Unable to save setting', message);
    } finally {
      setSavingKey(null);
    }
  };

  const renderUnavailable = (definition: EconomySettingDefinition) => (
    <div
      className="economy-setting-row economy-setting-row-unavailable"
      key={definition.key}
      data-testid={`economy-unavailable-${definition.key}`}
      role="status"
    >
      <div className="economy-setting-copy">
        <div className="economy-setting-heading">
          <h4>{definition.label}</h4>
          <span className="economy-setting-key">{definition.key}</span>
        </div>
        <p>{definition.description}</p>
      </div>
      <span className="economy-setting-unavailable">
        <AlertTriangle size={16} />
        Unavailable from API
      </span>
    </div>
  );

  const renderSetting = (definition: EconomySettingDefinition) => {
    const setting = settings[definition.key];
    if (!setting) return renderUnavailable(definition);

    const draftValue = drafts[definition.key] ?? setting.value;
    const parsedDraft = parseEconomySettingValue(definition, draftValue);
    const savedCanonicalValue = parseEconomySettingValue(definition, setting.value).parsed?.canonicalValue;
    const isDirty = parsedDraft.parsed
      ? parsedDraft.parsed.canonicalValue !== (savedCanonicalValue ?? setting.value)
      : draftValue !== setting.value;
    const fieldError = fieldErrors[definition.key];
    const isSaving = savingKey === definition.key;
    const isAnotherSaveRunning = savingKey !== null && !isSaving;

    return (
      <div className="economy-setting-row" key={definition.key} data-testid={`economy-setting-${definition.key}`}>
        <div className="economy-setting-copy">
          <div className="economy-setting-heading">
            <h4>{definition.label}</h4>
            <span className="economy-setting-key">{definition.key}</span>
          </div>
          <p>{setting.description || definition.description}</p>
          {definition.readOnly && definition.readOnlyReason && (
            <p className="economy-setting-note">
              <Lock size={14} /> {definition.readOnlyReason}
            </p>
          )}
          <div className="economy-setting-meta">
            <span>
              Last saved: {formatSettingUpdatedAt(setting.updated_at)}
            </span>
            <span>
              {setting.updated_by ? `Updated by ${setting.updated_by}` : 'Updated by unavailable'}
            </span>
          </div>
        </div>

        <div className="economy-setting-control">
          <div className="economy-setting-input-row">
            <input
              id={`economy-${definition.key}`}
              aria-label={definition.label}
              aria-invalid={fieldError ? 'true' : 'false'}
              aria-describedby={fieldError ? `economy-${definition.key}-error` : undefined}
              type={definition.valueType === 'int' ? 'number' : 'text'}
              inputMode={
                definition.valueType === 'int' ? 'numeric'
                  : definition.valueType === 'float' ? 'decimal' : 'text'
              }
              step={definition.valueType === 'string' ? undefined : definition.step}
              min={definition.min}
              max={definition.max}
              maxLength={definition.maxLength}
              value={draftValue}
              readOnly={definition.readOnly}
              disabled={definition.readOnly || isSaving || isAnotherSaveRunning}
              onChange={event => handleDraftChange(definition, event.target.value)}
            />
            <span className="economy-setting-unit">{definition.unit}</span>
          </div>
          {definition.readOnly ? (
            <span className="economy-setting-fixed">
              <Lock size={14} /> Fixed
            </span>
          ) : (
            <button
              type="button"
              className="save-btn compact economy-save-btn"
              aria-label={`Save ${definition.label}`}
              onClick={() => void handleSaveSetting(definition)}
              disabled={!isDirty || savingKey !== null}
            >
              {isSaving ? <RefreshCw className="spin" size={14} /> : <Save size={14} />}
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          )}
          {fieldError && (
            <p
              className="economy-setting-error"
              id={`economy-${definition.key}-error`}
              role="alert"
            >
              <AlertTriangle size={14} /> {fieldError}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="economy-settings-panel" data-testid="economy-settings-panel">
      <div className="economy-settings-intro">
        <div>
          <h3>Economy Settings</h3>
          <p>
            These values control new game sessions and future TP calculations.
            Every change is validated by the backend and recorded against your admin account.
          </p>
        </div>
        <div className="economy-settings-intro-actions">
          <button
            type="button"
            className="refresh-btn"
            onClick={() => void loadSettings()}
            disabled={loading || savingKey !== null}
          >
            <RefreshCw className={loading ? 'spin' : ''} size={16} />
            Refresh
          </button>
          <ShieldCheck size={28} aria-hidden="true" />
        </div>
      </div>

      <div className="economy-settings-contract" role="note">
        <Info size={16} />
        <span>
          Existing sessions and completed transactions keep their original values.
          Changes apply to new sessions and future calculations.
        </span>
      </div>

      {lastSave && (
        <div
          className={`economy-settings-confirmation ${lastSave.auditUnavailable ? 'warning' : ''}`}
          role="status"
          data-testid="economy-save-confirmation"
        >
          {lastSave.auditUnavailable ? (
            <AlertTriangle size={18} />
          ) : (
            <CheckCircle size={18} />
          )}
          <div>
            <strong>{lastSave.label} saved as {lastSave.value}.</strong>
            {lastSave.auditUnavailable ? (
              <span> The value was saved; refresh to retrieve its audit timestamp and actor.</span>
            ) : (
              <span>
                {' '}Audit recorded {lastSave.updatedBy ? `for ${lastSave.updatedBy}` : 'for the current admin'} at{' '}
                {formatSettingUpdatedAt(lastSave.updatedAt)}.
              </span>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-state" data-testid="economy-settings-loading">
          <RefreshCw className="spin" size={32} />
          <span>Loading economy settings…</span>
        </div>
      ) : loadError ? (
        <div className="economy-settings-load-error" role="alert">
          <AlertTriangle size={18} />
          <span>{loadError}</span>
          <button type="button" className="refresh-btn" onClick={() => void loadSettings()}>
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      ) : (
        ECONOMY_SETTING_GROUPS.map(group => {
          const definitions = ECONOMY_SETTING_DEFINITIONS.filter(
            definition => definition.group === group
          );
          return (
            <section className="economy-settings-group" key={group} aria-labelledby={getEconomyGroupId(group)}>
              <h4 className="settings-group-title" id={getEconomyGroupId(group)}>{group}</h4>
              <div className="economy-settings-list">
                {definitions.map(renderSetting)}
              </div>
            </section>
          );
        })
      )}
    </div>
  );
};

export default EconomySettingsPanel;
